import React, { useEffect, useState } from "react";
import {
    Drawer,
    Form,
    Input,
    Button,
    message,
    DatePicker,
    Select,
    InputNumber,
    Modal,
    Row,
    Col,
    Table,
} from "antd";
import API from "../../api";
import moment from "moment";
import dayjs from "dayjs";

const { Option } = Select;

// Helper function to format month display
const formatMonthDisplay = (month) => {
    if (!month) return 'N/A';
    
    // Parse month string (YYYY-MM format)
    const [year, monthNum] = month.split('-');
    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const monthName = monthNames[parseInt(monthNum) - 1] || 'Unknown';
    return `${monthName} ${year}`;
};

const EmployeeCommissionDrawer = ({ open, setOpen, employeeId, month, salesSummary, onCommissionCreated }) => {
    const [form] = Form.useForm();
    const [isLoading, setLoading] = useState(false);
    const [sales, setSales] = useState([]);
    const [stores, setStores] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);

    const [salesData, setSalesData] = useState([]);
    const [salesSummaryData, setSalesSummaryData] = useState({
        salesCount: 0,
        totalAmount: "0.00",
        totalAmountWithoutTax: "0.00",
    });
    const [commissionSummary, setCommissionSummary] = useState({
        commissionCount: 0,
        totalCommissionPaid: "0.00",
    });
    const [selectedSalesCount, setSelectedSalesCount] = useState(0);
    const [totalSalesAmount, setTotalSalesAmount] = useState(0);
    const [commissionType, setCommissionType] = useState('percentage');
    const [commissionPercentage, setCommissionPercentage] = useState(0);
    const [calculatedAmount, setCalculatedAmount] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState('');
    const userId = JSON.parse(localStorage.getItem("userData"))?.id;


    const defaultData = {
        date: null,
        saleIds: [],
        employeeId: employeeId || "",
        amount: 0,
        paymentMethod: null,
        bill: "",
        description: "",
        store: "",
        month: null, // Add month field
        createdBy: userId || "",
        status: 'paid',
    };

    // Function to generate date range from month
    const getDateRangeFromMonth = (month) => {
        if (!month) return { fromDate: null, toDate: null };
        const monthStart = dayjs(month).startOf('month');
        const monthEnd = dayjs(month).endOf('month');
        return {
            fromDate: monthStart.format("YYYY-MM-DD"),
            toDate: monthEnd.format("YYYY-MM-DD")
        };
    };

    const getEmployees = async () => {
        try {
            const response = await API.get("/employee");
            setEmployees(response?.data || []);
        } catch (err) {
            console.error("Error fetching employees:", err);
            message.error("Failed to load employees.");
        }
    };
    const fetchCommissionSummary = async () => {
        if (!employeeId || !month) return;
        
        try {
            const response = await API.get("/employee-commissions/paginated", {
                params: {
                    employeeId,
                    month: month,
                    status: 'paid',
                    page: 1,
                    limit: 1000, // Get all paid commissions
                },
            });
            
            const commissions = response?.data?.commissions || [];
            const commissionCount = commissions.length;
            const totalCommissionPaid = commissions.reduce((sum, commission) => sum + (commission.amount || 0), 0);
            
            setCommissionSummary({
                commissionCount,
                totalCommissionPaid: totalCommissionPaid.toFixed(2),
            });
        } catch (err) {
            console.error("Error fetching commission summary:", err);
            setCommissionSummary({
                commissionCount: 0,
                totalCommissionPaid: "0.00",
            });
        }
    };

    const handleCommissionTypeChange = (value) => {
        setCommissionType(value);
        if (value === 'percentage') {
            setCommissionPercentage(0);
            setCalculatedAmount(0);
            form.setFieldsValue({ 
                commissionPercentage: 0,
                amount: 0 
            });
        } else {
            setCalculatedAmount(0);
            form.setFieldsValue({ amount: 0 });
        }
    };

    const handleCommissionPercentageChange = (value) => {
        setCommissionPercentage(value || 0);
        const calculated = totalSalesAmount * (value || 0) / 100;
        setCalculatedAmount(calculated);
        form.setFieldsValue({ amount: calculated });
    };

    const handleFixedAmountChange = (value) => {
        setCalculatedAmount(value || 0);
        form.setFieldsValue({ amount: value || 0 });
    };

    const handlePaymentMethodChange = (value) => {
        setPaymentMethod(value);
        // Clear bank account selection when switching to cash
        if (value === 'cash') {
            form.setFieldsValue({ bankAccount: undefined });
        }
    };

    const fetchSales = async () => {
        if (!employeeId || !month) return;
        
        // Generate date range from month
        const { fromDate, toDate } = getDateRangeFromMonth(month);
        
        try {
            const response = await API.get("/sales/employee", {
                params: {
                    employeeId,
                    fromDate,
                    toDate,
                    page: 1,
                    limit: 1000, // Increased limit to get all sales
                },
            });
            const salesData = response?.data?.sales || [];
            const paidCommissionSales = await API.get("/employee-commissions/paginated", {
                params: {
                    employeeId,
                    month: month,
                    status: 'paid',
                    page: 1,
                    limit: 1000, // Get all commissions to check against
                },
            });
            const paidSaleIds = new Set();
            paidCommissionSales?.data?.commissions.forEach(commission => {
                if (commission.saleIds && Array.isArray(commission.saleIds)) {
                    commission.saleIds.forEach(sale => {
                        if (sale && sale._id) {
                            paidSaleIds.add(sale._id.toString());
                        }
                    });
                }
            });
            const eligibleSales = salesData.filter(sale => 
                !paidSaleIds.has(sale._id.toString()) && 
                sale.status === 'completed' // Only include completed sales
            );
            setSales(eligibleSales);
            setSalesData(salesData);
            setSalesSummaryData(response?.data?.summary || {
                salesCount: 0,
                totalAmount: "0.00",
                totalAmountWithoutTax: "0.00",
            });

            // Calculate total sales amount (including GST)
            const totalAmount = eligibleSales.reduce((sum, sale) => sum + (sale.totalAmount || 0), 0);
            setTotalSalesAmount(totalAmount);

            // Auto-select all eligible sales and set month
            if (eligibleSales.length > 0) {
                const allEligibleSaleIds = eligibleSales.map(sale => sale._id);
                form.setFieldsValue({
                    saleIds: allEligibleSaleIds,
                    month: month, // Set the month
                    employeeId: employeeId, // Set the employee ID
                    commissionType: 'percentage',
                    commissionPercentage: 0
                });
                setSelectedSalesCount(allEligibleSaleIds.length);
                const monthDisplay = formatMonthDisplay(month);
                message.success(`Found ${eligibleSales.length} unpaid invoices for ${monthDisplay} commission payment`);
            } else {
                setSelectedSalesCount(0);
                setTotalSalesAmount(0);
                message.warning("No unpaid invoices found for the selected employee and month");
            }
        } catch (err) {
            console.error("Error fetching sales:", err);
            message.error("Failed to load sales for selection.");
        }
    };

    const fetchStores = async () => {
        try {
            const response = await API.get("/stores");
            setStores(response?.data || []);
        } catch (err) {
            console.error("Error fetching stores:", err);
            message.error("Failed to load stores.");
        }
    };

    const fetchBankAccounts = async () => {
        try {
            const response = await API.get("/bank-accounts/paginate", {
                params: {
                    page: 1,
                    limit: 1000 // Get all bank accounts
                }
            });
            setBankAccounts(response?.data?.bankAccounts || []);
        } catch (err) {
            console.error("Error fetching bank accounts:", err);
            message.error("Failed to load bank accounts.");
        }
    };

    useEffect(() => {
        if (open) {
            fetchSales();
            fetchCommissionSummary();
            getEmployees();
            fetchStores();
            fetchBankAccounts();
            form.setFieldsValue({
                ...defaultData,
                employeeId,
                date: moment(),
            });
        }
    }, [open, employeeId, month, form]);





    const onFinish = async (values) => {
        const { saleIds, ...rest } = values;
        
        // Validate if no invoices are selected
        if (!saleIds || !Array.isArray(saleIds) || saleIds.length === 0) {
            message.error("Please select at least one invoice to create commission.");
            return;
        }

        // Debug: Log form values to see what's being submitted
        console.log("Form values:", values);
        console.log("Required fields check:", {
            date: values.date,
            saleIds: saleIds,
            employeeId: employeeId,
            amount: values.amount,
            createdBy: userId,
            store: values.store,
            month: month,
            commissionType: values.commissionType
        });
        
        // Show confirmation dialog
        Modal.confirm({
            title: 'Confirm Commission Payment',
            content: (
                <div>
                    <p>Are you sure you want to create commission payment for:</p>
                    <ul style={{ margin: '10px 0', paddingLeft: '20px' }}>
                        <li><strong>Employee:</strong> {employees.find(emp => emp._id === employeeId)?.name || 'Unknown'}</li>
                        <li><strong>Month:</strong> {formatMonthDisplay(month)}</li>
                        <li><strong>Selected Invoices:</strong> {saleIds.length}</li>
                        <li><strong>Commission Amount:</strong> ₹{values.amount || 0}</li>
                    </ul>
                    <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>This action cannot be undone.</p>
                </div>
            ),
            okText: 'Yes, Create Commission',
            cancelText: 'Cancel',
            okType: 'primary',
            onOk: async () => {
                setLoading(true);
                try {
                    // Create single commission record with array of saleIds
                    const commission = {
                        date: values.date ? values.date.toISOString() : null,
                        saleIds: saleIds, // Array of sale IDs
                        employeeId: employeeId,
                        amount: values.amount || calculatedAmount,
                        paymentMethod: values.paymentMethod || null,
                        description: values.description || null,
                        createdBy: userId,
                        store: values.store,
                        month: month, // Use the month prop
                        status: 'paid',
                        commissionType: values.commissionType,
                        commissionPercentage: values.commissionPercentage || null,
                        totalSalesAmount: totalSalesAmount,
                        bankAccount: values.bankAccount || null, // Store account number directly
                    };
                    await API.post("/employee-commissions", commission);
                    form.resetFields();
                    setOpen(false);
                    message.success(`Added commissions for ${saleIds.length} sale(s) successfully`);
                    fetchSales(); // Refresh sales after submission
                    fetchCommissionSummary(); // Refresh commission summary after submission
                    onCommissionCreated && onCommissionCreated(); // Notify parent component
                } catch (err) {
                    console.error("Error submitting form:", err);
                    message.error("Failed to submit the form. Please try again.");
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const salesColumns = [
        {
            title: "Index",
            key: "index",
            render: (_, __, index) => index + 1,
        },
        {
            title: "Inv. No.",
            render: (_, sale) => sale?.invoiceId,
            key: "invoiceId",
        },
        {
            title: "Inv. Date",
            key: "orderDate",
            render: (_, sale) => moment(sale?.orderDate).format("DD-MM-YYYY"),
        },
        {
            title: "Inv. Amount (₹)",
            key: "totalAmount",
            render: (_, sale) =>
                `₹ ${new Intl.NumberFormat().format(sale?.totalAmount)}`,
        },
        {
            title: "Customer",
            render: (_, sale) => sale?.customerId?.name,
            key: "customerName",
        },
        {
            title: "Store",
            key: "storeName",
            render: (_, sale) => sale?.storeId?.name || "N/A",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => {
                let color;
                if (status === "pending") color = "yellow";
                else if (status === "completed") color = "green";
                else if (status === "cancelled") color = "red";
                else if (status === "replace") color = "orange";
                return (
                    <span style={{ color, fontWeight: "bold" }}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                    </span>
                );
            },
        },
    ];

    return (
        <Drawer
            title="Pay Commission"
            width={800}
            placement="right"
            onClose={() => setOpen(false)}
            open={open}
            destroyOnClose
        >
            {/* Sales Summary */}
            {totalSalesAmount > 0 && (
                <div style={{ 
                    marginBottom: '20px',
                    padding: '16px',
                    backgroundColor: '#e8f5e8',
                    borderRadius: '8px',
                    border: '1px solid #c3e6c3'
                }}>
                    <div style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        marginBottom: '8px',
                        color: '#2d5a2d'
                    }}>
                        Sales Summary ({formatMonthDisplay(month)})
                    </div>
                    <div style={{ 
                        fontSize: '18px', 
                        fontWeight: '700',
                        color: '#1a4a1a'
                    }}>
                        Total Sales Amount: ₹{new Intl.NumberFormat().format(totalSalesAmount)}
                    </div>
                </div>
            )}

            <Form
                layout="vertical"
                form={form}
                onFinish={onFinish}
                autoComplete="off"
                initialValues={defaultData}
            >
                <Row gutter={[10, 0]}>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Date"
                            name="date"
                            rules={[{ required: true, message: "Please select the date." }]}
                        >
                            <DatePicker style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Employee"
                            name="employeeId"
                            rules={[{ required: true, message: "Employee is required." }]}
                        >
                            <Input disabled value={employeeId} style={{ display: "none" }} />
                            <Input
                                disabled
                                value={employees.find((emp) => emp._id === employeeId)?.name || ""}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Commission Type"
                            name="commissionType"
                            rules={[{ required: true, message: "Please select commission type." }]}
                        >
                            <Select 
                                value={commissionType}
                                onChange={handleCommissionTypeChange}
                                style={{ width: "100%" }}
                            >
                                <Option value="percentage">Percentage (%)</Option>
                                <Option value="fixed">Fixed Amount (₹)</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    
                    {commissionType === 'percentage' ? (
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Commission Percentage"
                                name="commissionPercentage"
                                rules={[
                                    { required: true, message: "Please enter commission percentage." },
                                    { type: "number", min: 0, max: 100, message: "Percentage must be between 0-100." },
                                ]}
                            >
                                <InputNumber 
                                    style={{ width: "100%" }} 
                                    min={0} 
                                    max={100} 
                                    step={0.01}
                                    suffix="%"
                                    onChange={handleCommissionPercentageChange}
                                />
                            </Form.Item>
                        </Col>
                    ) : (
                        <Col xs={24} sm={12}>
                            <Form.Item
                                label="Commission Amount"
                                name="amount"
                                rules={[
                                    { required: true, message: "Please enter the commission amount." },
                                    { type: "number", min: 0, message: "Amount must be a positive number." },
                                ]}
                            >
                                <InputNumber 
                                    style={{ width: "100%" }} 
                                    min={0} 
                                    step={0.01}
                                    onChange={handleFixedAmountChange}
                                />
                            </Form.Item>
                        </Col>
                    )}
                    
                    {/* Calculated Amount Display */}
                    {calculatedAmount > 0 && (
                        <Col xs={24}>
                            <div style={{ 
                                padding: '12px',
                                backgroundColor: '#f0f8ff',
                                borderRadius: '6px',
                                border: '1px solid #b3d9ff',
                                textAlign: 'center'
                            }}>
                                <div style={{ fontSize: '14px', color: '#666', marginBottom: '4px' }}>
                                    {commissionType === 'percentage' ? 'Calculated Commission Amount' : 'Entered Commission Amount'}
                                </div>
                                <div style={{ fontSize: '20px', fontWeight: '700', color: '#1890ff' }}>
                                    ₹{new Intl.NumberFormat().format(calculatedAmount)}
                                </div>
                            </div>
                        </Col>
                    )}
                    <Col xs={24} sm={12}>
                        <Form.Item label="Payment Method" name="paymentMethod">
                            <Select 
                                placeholder="Select payment method" 
                                allowClear
                                onChange={handlePaymentMethodChange}
                            >
                                <Option value="cash">Cash</Option>
                                <Option value="upi">UPI</Option>
                                <Option value="bank_transfer">Bank Transfer</Option>
                                <Option value="other">Other</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    
                    {/* Bank Account Selection - Only show for non-cash payments */}
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Bank Account"
                            name="bankAccount"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (paymentMethod && paymentMethod !== 'cash' && !value) {
                                            return Promise.reject(new Error('Please select a bank account for non-cash payments.'));
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <Select 
                                placeholder="Select bank account" 
                                allowClear
                                disabled={paymentMethod === 'cash' || !paymentMethod}
                            >
                                {bankAccounts.map((account) => (
                                    <Option key={account._id} value={account.accountNumber}>
                                        {account.bankName} - {account.accountNumber} ({account.accountHolderName})
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Store"
                            name="store"
                            rules={[{ required: true, message: "Please select a store." }]}
                        >
                            <Select placeholder="Select a store" allowClear>
                                {stores.map((store) => (
                                    <Option key={store._id} value={store._id}>
                                        {store.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Month"
                            name="month"
                            rules={[{ required: true, message: "Month is required." }]}
                        >
                            <Input 
                                placeholder="YYYY-MM (e.g., 2024-01)" 
                                disabled 
                                style={{ backgroundColor: '#f5f5f5' }}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Created By"
                            name="createdBy"
                            rules={[{ required: true, message: "Created by is required." }]}
                            style={{ display: "none" }}
                        >
                            <Input value={userId} disabled />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                        <Form.Item
                            label="Status"
                            name="status"
                            rules={[{ required: true, message: "Status is required." }]}
                            style={{ display: "none" }}
                        >
                            <Input value="paid" disabled />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item label="Description" name="description">
                            <Input.TextArea rows={2} placeholder="Enter description (optional)" />
                        </Form.Item>
                    </Col>
                    <Col xs={24}>
                        <Form.Item>
                            <Button 
                                type="primary" 
                                block 
                                htmlType="submit" 
                                loading={isLoading}
                                disabled={selectedSalesCount === 0}
                            >
                                {selectedSalesCount > 0 
                                    ? `Create Commission Payment (${selectedSalesCount} invoices)` 
                                    : 'No Invoices Available'
                                }
                            </Button>
                        </Form.Item>
                    </Col>
                    
                    {/* Hidden field for saleIds validation */}
                    <Col xs={24} style={{ display: 'none' }}>
                        <Form.Item
                            name="saleIds"
                            rules={[
                                { required: true, message: "No invoices available for commission." },
                                {
                                    validator: (_, value) => {
                                        if (!value || !Array.isArray(value) || value.length === 0) {
                                            return Promise.reject(new Error('No invoices available for commission.'));
                                        }
                                        return Promise.resolve();
                                    }
                                }
                            ]}
                        >
                            <Input />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            
            {/* Selected Invoices Section */}
            <div style={{ 
                marginTop: '10px',
                padding: '5px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                border: '1px solid #e9ecef'
            }}>
                <div style={{ 
                    fontSize: '16px', 
                    fontWeight: '600', 
                    marginBottom: '12px',
                    color: '#495057'
                }}>
                    Selected Invoices ({sales.length})
                </div>
                
                {sales.length > 0 ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {sales.map((sale, index) => (
                            <div key={sale._id} style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '2px 2px',
                                backgroundColor: '#fff',
                                borderRadius: '6px',
                                border: '1px solid #dee2e6',
                                fontSize: '10px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                            }}>
                                <span style={{ color: '#28a745', marginRight: '6px', fontSize: '14px' }}>✓</span>
                                <span style={{ fontWeight: '600', color: '#495057' }}>{sale.invoiceId}</span>
                                <span style={{ color: '#6c757d', marginLeft: '8px' }}>
                                    ₹{new Intl.NumberFormat().format(sale.totalAmount)}
                                </span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div style={{ 
                        color: '#6c757d', 
                        textAlign: 'center', 
                        padding: '20px',
                        fontStyle: 'italic'
                    }}>
                        No unpaid invoices available for this employee and month
                    </div>
                )}
            </div>
        </Drawer>
    );
};

export default EmployeeCommissionDrawer;